import { apiService } from './api'
import { LoginCredentials, RegisterData, AuthTokens, User, ApiResponse } from '@/types'

export class AuthService {
  /**
   * Register a new user
   */
  async register(userData: RegisterData): Promise<ApiResponse<{ user: User; tokens: AuthTokens }>> {
    return await apiService.post('/auth/register/', userData)
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ user: User; tokens: AuthTokens }>> {
    return await apiService.post('/auth/login/', credentials)
  }

  /**
   * Logout user (blacklist refresh token)
   */
  async logout(refreshToken: string): Promise<ApiResponse<void>> {
    return await apiService.post('/auth/logout/', { refresh: refreshToken })
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<ApiResponse<{ access: string }>> {
    return await apiService.post('/auth/refresh/', { refresh: refreshToken })
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<ApiResponse<void>> {
    return await apiService.post('/auth/password/reset/', { email })
  }

  /**
   * Confirm password reset with token
   */
  async confirmPasswordReset(
    uid: string,
    token: string,
    password: string,
    passwordConfirm: string
  ): Promise<ApiResponse<void>> {
    return await apiService.post('/auth/password/reset/confirm/', {
      uid,
      token,
      new_password: password,
      new_password_confirm: passwordConfirm
    })
  }

  /**
   * Change password (requires authentication)
   */
  async changePassword(
    oldPassword: string, 
    newPassword: string, 
    newPasswordConfirm: string
  ): Promise<ApiResponse<void>> {
    return await apiService.post('/auth/password/change/', {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm
    })
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return await apiService.get('/users/me/')
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: Partial<User>): Promise<ApiResponse<User>> {
    return await apiService.put('/users/update_profile/', profileData)
  }

  /**
   * Update accessibility preferences
   */
  async updateAccessibilityPreferences(preferences: any): Promise<ApiResponse<void>> {
    return await apiService.put('/users/update_preferences/', preferences)
  }

  /**
   * Update notification preferences
   */
  async updateNotificationPreferences(preferences: any): Promise<ApiResponse<void>> {
    return await apiService.put('/users/update_notifications/', preferences)
  }

  /**
   * Get user statistics
   */
  async getUserStatistics(period: string = 'month'): Promise<ApiResponse<any>> {
    return await apiService.get(`/users/stats/?period=${period}`)
  }

  /**
   * Export user data as a downloadable JSON file.
   * Fetches tracks and POIs from the API and triggers a browser download.
   */
  async exportUserData(): Promise<ApiResponse<{ message: string }>> {
    try {
      const [tracksRes, poisListRes, profileRes] = await Promise.all([
        apiService.get<any>('/tracks/?limit=10000'),
        apiService.get<any>('/pois/?created_by=me&limit=10000'),
        apiService.get<any>('/users/me/')
      ])

      // Fetch full POI details with ratings for each POI
      const poisWithRatings = await Promise.all(
        poisListRes.data.results.map((poi: any) =>
          apiService.get<any>(`/pois/${poi.id}/`)
        )
      )

      const exportPayload = {
        exportDate: new Date().toISOString(),
        profile: profileRes.data,
        tracks: tracksRes.data,
        pois: {
          count: poisListRes.data.count,
          next: poisListRes.data.next,
          previous: poisListRes.data.previous,
          results: poisWithRatings.map(res => res.data)
        }
      }

      const json = JSON.stringify(exportPayload, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const filename = `matt-data-${new Date().toISOString().split('T')[0]}.json`

      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      URL.revokeObjectURL(url)

      return { data: { message: 'export_success' }, error: null }
    } catch (error: any) {
      return { data: null, error: error.message || 'Export failed' }
    }
  }

  /**
   * Request permanent account deletion
   * Sends a deletion request to admins via Slack notification
   */
  async deleteAccount(email: string, reason?: string): Promise<ApiResponse<{ message: string }>> {
    return apiService.post<{ message: string }>('/account-deletion-request/', { email, reason })
  }
}

// Export singleton instance
export const authService = new AuthService()
export default authService